import React, { useContext } from 'react';
import PropTypes from 'prop-types';

import {
	Typography,
	makeStyles
} from '@material-ui/core';

import Constants from '../utils/Constants';
import HTMLEntities from "../utils/HTMLEntities";
import { useHasImage, useReplaceImageTags } from "../utils/CustomHooks";
import TwitterRedaction from "./TwitterRedaction";
import GSuiteComments from "./GSuiteComments";
import MoreOrLess from "./MoreOrLess";
import ReadMore from './ReadMore';
import Diff from "./Diff";
import HangoutsChatContent from "./HangoutsChatContent";
import GoogleChatContent from './GoogleChatContent';
import AuthorizedUserContext from '../utils/AuthorizedUserContext';

const styles = makeStyles(theme => ({
	contentWrap: {
		fontWeight: 300,
		position: "relative",
		color: Constants.COLORS.SECONDARY.GREY,
		overflowWrap: "break-word",
		marginBottom: theme.spacing(1)
	},

	redactionMsg: {
		marginBottom: theme.spacing(1)
	}
}));

function ActivityContent(props) {
	let classes = styles(),
		{ content, originalContent, patch, patchRender, color,
			priorComments, anchorText, time, authorName, libraryTopics, inlineReadMore, rightReadMore, previousMessages,
			sourceId, subSourceId, sourceIcon, boldWords, readMoreLines, twitterRedacted, history, expanded, category, extraHeaders, searchTerm } = props;

	const hasImage = useHasImage(patch ? patch : anchorText);
	const authenticatedUser = useContext(AuthorizedUserContext);

	patch = useReplaceImageTags(patch);
	anchorText = useReplaceImageTags(HTMLEntities.decode(anchorText));

	const makeBold = (content, words) => {
		if (content && words && words.length) {
			content = words.reduce((result, theWords) => {
				let regex = new RegExp(`\\b(${RegExp.escape(theWords)})\\b`, "ig");
				return result.replace(regex, "<b>$1</b>");
			}, content);
		}

		return content;
	};

	const formatContent = (content) => {		
		if(content) {
			const regex = /\>(\>*)/g;
			const replacement = "<br />$1";
			content = content.replace(regex, replacement);
			const updatedRegex = /<br\s*\/>\s*>+/g;
			const updatedReplacement = "<br />";
			content = content.replace(updatedRegex, updatedReplacement);
		}

		return content;
	};

	const highlightText = (text) => {
		if (!searchTerm || searchTerm.length <= 2) return text;

		const regex = new RegExp(`(${searchTerm})`, 'gi');
		const highlightedText = HTMLEntities.decode(text).replace(
			regex,
			(match) => `<span style='background-color: #f9894b;'>${match}</span>`
		);
		return highlightedText;
	};

	let hasBoldWords = boldWords && boldWords.length ? true : false,
		isHangoutsChat = sourceId === Constants.SOURCES.IDS.HANGOUTS_CHAT,
		isGSuiteComment = subSourceId === Constants.SOURCES.IDS.GSUIT_COMMENT,
		isGoogleChat = sourceId === Constants.SOURCES.IDS.GOOGLE_CHAT;
	if (patch == null) {
		const accountId = authenticatedUser.accountId;
		const env = process.env.REACT_APP_DEV_ENV;
		if ((accountId === 661) ||
			(env === 'prod' && (accountId === 477 || accountId === 2123))) {
			const regex = /\((https?:\/\/[^)]+)\)/g;
			content = content.replace(regex, '<br>');
			originalContent = originalContent ? originalContent.replace(regex, '<br>') : originalContent;
		}
		const formattedContent = formatContent(HTMLEntities.decode(content));
		content = highlightText(formattedContent);
		originalContent = formatContent(HTMLEntities.decode(originalContent));
	}

	/***********************************************************************
		TODO: Temporarily only preserveHtml when there are words to bold
			to fix the browser freeze bug. Need to figure out a
			better way to truncate and also allow html. Current
			idea is to use CSS, determine if there is overflow,
			use xss to sanitize the html and use dangerouslySetInnerHTML
	***********************************************************************/

	return twitterRedacted && twitterRedacted !== Constants.ACTIVITY.TWITTER_REDACTION.SCRUB_GEO ? (
		<TwitterRedaction
			className={classes.redactionMsg}
			twitterRedacted={twitterRedacted}
			history={history}
			isAlert={category === Constants.ACTIVITY.CATEGORIES.ALERTS.ID} />
	) : (
		<Typography color={color} component="div" className={classes.contentWrap}>
			{isGSuiteComment ? (
				<GSuiteComments
					hasImage={hasImage}
					content={content}
					time={time}
					authorName={authorName}
					priorComments={priorComments}
					anchorText={anchorText}
					sourceIcon={sourceIcon}
					libraryTopics={libraryTopics} />
			) : patch ? patchRender ? patchRender(patch) : (
				<MoreOrLess
					hasImage={hasImage}
					expanded={expanded}
					inline={inlineReadMore}
					right={rightReadMore}
					content={<Diff patch={patch} />} />
			) : hasBoldWords ? (
				<ReadMore
					showInline
					lines={readMoreLines}
					preserveHtml={hasBoldWords}
					html={makeBold(content, boldWords)}
					originalContent={makeBold(originalContent, boldWords)} />
			) : isHangoutsChat ? (
				<HangoutsChatContent
					content={content}
					readMoreLines={readMoreLines}
					originalContent={originalContent} />
			) : isGoogleChat ? (
				<GoogleChatContent
					content={content}
					extraHeaders={extraHeaders}
					previousMessages={previousMessages} />
			) : (
				<ReadMore
					key={content}
					showInline
					lines={readMoreLines}
					html={content}
					fromActivityContent={true}
					originalContent={originalContent}>
					<div dangerouslySetInnerHTML={{ __html: content }} />
				</ReadMore>
			)}
		</Typography>
	);
}

ActivityContent.defaultProps = {
	readMoreLines: 7
};

ActivityContent.propTypes = {
	content: PropTypes.string,
	patch: PropTypes.string,
	patchRender: PropTypes.func,
	originalContent: PropTypes.string,
	color: PropTypes.string,
	boldWords: PropTypes.array,
	readMoreLines: PropTypes.number,
	twitterRedacted: PropTypes.string,
	history: PropTypes.object,
	category: PropTypes.number,
	sourceId: PropTypes.number,
	subSourceId: PropTypes.string,
	sourceIcon: PropTypes.object,
	libraryTopics: PropTypes.array,
	priorComments: PropTypes.array,
	anchorText: PropTypes.string,
	time: PropTypes.string,
	authorName: PropTypes.string,
	expanded: PropTypes.bool,
	inlineReadMore: PropTypes.bool,
	rightReadMore: PropTypes.bool,
	extraHeaders: PropTypes.object
};

export default ActivityContent;